/**
 * Send `body` as a JSON request. The Content-Type header and the serialisation
 * are one decision, not two, and every JSON write in the app made it separately.
 *
 * Returns the raw Response. The callers genuinely differ in how they read a
 * failure - a thrown error, a status chip, a specific error code off the body,
 * or nothing at all - so only the request itself belongs here.
 */
export function sendJson(
  method: "POST" | "PUT" | "PATCH",
  url: string,
  body: unknown,
  /** The streaming agent calls abort in-flight requests; nothing else needs this. */
  options?: { signal?: AbortSignal | null },
): Promise<Response> {
  return fetch(url, {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    // Spread rather than always setting `signal: undefined`: the tests that assert
    // the exact request init should keep seeing the init they always saw.
    ...(options?.signal ? { signal: options.signal } : {}),
  });
}

export function postJson(
  url: string,
  body: unknown,
  options?: { signal?: AbortSignal | null },
): Promise<Response> {
  return sendJson("POST", url, body, options);
}

export function putJson(url: string, body: unknown): Promise<Response> {
  return sendJson("PUT", url, body);
}

/**
 * Which bound refused the request. Mirrors the `Bound` union that
 * `src/lib/api/routeHelpers.ts` declares and that the refusal envelope carries.
 *
 * It is re-declared here rather than imported because that module pulls
 * NextResponse, prisma and both bound modules, none of which belongs in a
 * browser bundle. The union is the rendering DISCRIMINATOR: `globalDaily` is
 * "closed for everyone right now" and the other three are "that's yours for
 * today", which is the only thing the copy branches on.
 */
export type RateLimitScope = "request" | "userDaily" | "globalDaily" | "regenerate";

/**
 * A refusal, typed (ABUSE-10).
 *
 * A REFUSAL IS AN EXPECTED ANSWER, NOT AN ERROR. Three places it must never
 * reach, and they are the whole of the rule:
 *
 * - an error logger (no `console.error`),
 * - an error tracker (no Sentry capture),
 * - a cold-failure state (no full-screen error, no toast, no red banner).
 *
 * It renders as the state of whatever the learner just did: an active
 * conversation closes in character, and everything else gates the control that
 * made the call. That rule is 41-UI-SPEC.md Contract 2's, and it is the
 * difference between the designed behaviour and the error card ABUSE-12 exists
 * to remove.
 *
 * It extends Error so a caller that genuinely wants to reject a promise with it
 * can, not because anything is expected to throw it. The web has ONE fetch seam
 * and no query layer, so there is nothing here to re-fire it (ABUSE-11); see
 * `src/test/lib/http.test.ts`, which asserts that rather than assuming it.
 */
export class RateLimitedError extends Error {
  readonly code = "RATE_LIMITED" as const;
  readonly scope: RateLimitScope;
  readonly limit: number;
  /** Seconds until the binding bound's own bucket rolls over. */
  readonly retryAfter: number;
  readonly degrade: boolean;

  constructor(fields: {
    scope: RateLimitScope;
    limit: number;
    retryAfter: number;
    degrade: boolean;
  }) {
    super("Rate limited");
    this.name = "RateLimitedError";
    this.scope = fields.scope;
    this.limit = fields.limit;
    this.retryAfter = fields.retryAfter;
    this.degrade = fields.degrade;
  }
}

const SCOPES: readonly RateLimitScope[] = ["request", "userDaily", "globalDaily", "regenerate"];

function toScope(value: unknown): RateLimitScope {
  // "request" when the body cannot say. The scope only chooses between two
  // reason lines, and telling a learner "that's your conversation for today" is
  // both the narrower claim and the less alarming one when we do not know.
  return SCOPES.includes(value as RateLimitScope) ? (value as RateLimitScope) : "request";
}

function toSeconds(value: unknown): number | null {
  const n = typeof value === "string" ? Number(value) : value;
  return typeof n === "number" && Number.isFinite(n) && n >= 0 ? Math.trunc(n) : null;
}

/**
 * Read a refusal off a response, or nothing (41-UI-SPEC Contract 2).
 *
 * Deliberately NOT a global throw, and `sendJson` above is deliberately left
 * returning a raw Response: its doc comment records that callers genuinely
 * differ in how they read a failure, and converting the sender would break the
 * ones that render a status chip.
 *
 * `retryAfter` is read from the BODY first and falls back to the `Retry-After`
 * header, because plan 41-08 puts the same figure in both and the body value is
 * the one derived per binding bound. THIS IS WHAT LETS THE CLOSED CONVERSATION
 * PHASE RENDER WITH ZERO NETWORK: the learner-visible reset time is derived from
 * this number in client state, with no additional request.
 *
 * A malformed body degrades to the header rather than throwing. A parse helper
 * that throws on a malformed refusal turns a bounded state into a crash, which
 * is the failure this whole contract removes.
 *
 * The caller's response body is NOT consumed: the read happens on a clone, so a
 * caller that also wants to read the body still can.
 */
export async function parseRateLimited(res: Response): Promise<RateLimitedError | null> {
  if (res.status !== 429) return null;

  let body: Record<string, unknown> = {};
  try {
    const parsed: unknown = await res.clone().json();
    if (parsed && typeof parsed === "object") body = parsed as Record<string, unknown>;
  } catch {
    // A proxy's HTML, an empty body, a truncated stream. The header still tells
    // the learner when the door opens again, which is the field that matters.
  }

  const retryAfter = toSeconds(body.retryAfter) ?? toSeconds(res.headers.get("Retry-After")) ?? 0;

  return new RateLimitedError({
    scope: toScope(body.scope),
    limit: toSeconds(body.limit) ?? 0,
    // Every refusal means the learner is past a bound, so the degraded surface
    // set (D-05, D-06) is what they get. `false` is only honoured when the body
    // says so explicitly.
    degrade: body.degrade === false ? false : true,
    retryAfter,
  });
}
